package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * M5-e：suspend 关键字强制显式校验（E625 / E626 / E627）。
 *
 * 扫描每个 fun / lambda body，检测是否含 await 表达式（不递归进入嵌套 fun/lambda）：
 * - fun 含 await 但 `isSuspend = false` → E626
 * - lambda 含 await 但 `isSuspend = false` → E625
 * - fun/lambda 声明 suspend 但 body 无 await → E627（warning）
 */
class SuspendKeywordChecker(private val diag: DiagnosticCollector) {

    fun check(screen: ScreenAst) {
        // screen 级 fun
        for (funDecl in screen.funs) {
            val hasAwait = containsAwait(funDecl.body)
            if (!funDecl.isSuspend && hasAwait) {
                diag.error(DslError.E626_SUSPEND_FUN_MISSING_KEYWORD, funDecl.position, funDecl.name)
            } else if (funDecl.isSuspend && !hasAwait) {
                diag.warning(DslError.E627_SUSPEND_DECLARED_NO_AWAIT, funDecl.position, "fun ${funDecl.name}")
            }
            // 递归检查 body 内嵌套 fun/lambda
            checkStmts(funDecl.body)
        }
    }

    // ==================== 递归遍历：查找嵌套 fun/lambda 并校验 ====================

    private fun checkStmts(stmts: List<StmtAst>) {
        for (s in stmts) checkStmt(s)
    }

    private fun checkStmt(s: StmtAst) {
        when (s) {
            is StmtAst.FunDeclStmt -> {
                val hasAwait = containsAwait(s.decl.body)
                if (!s.decl.isSuspend && hasAwait) {
                    diag.error(DslError.E626_SUSPEND_FUN_MISSING_KEYWORD, s.decl.position, s.decl.name)
                } else if (s.decl.isSuspend && !hasAwait) {
                    diag.warning(DslError.E627_SUSPEND_DECLARED_NO_AWAIT, s.decl.position, "fun ${s.decl.name}")
                }
                checkStmts(s.decl.body)
            }
            is StmtAst.VarDeclStmt -> checkExpr(s.initializer)
            is StmtAst.AssignStmt -> checkExpr(s.rhs)
            is StmtAst.ExprStmt -> checkExpr(s.call)
            is StmtAst.IfStmt -> {
                checkExpr(s.cond)
                checkStmts(s.thenBody)
                checkStmts(s.elseBody)
            }
            is StmtAst.WhileStmt -> {
                checkExpr(s.cond)
                checkStmts(s.body)
            }
            is StmtAst.ForInStmt -> {
                checkExpr(s.iterable)
                checkStmts(s.body)
            }
            is StmtAst.WhenStmt -> checkExpr(s.whenExpr)
            is StmtAst.ReturnStmt -> s.value?.let { checkExpr(it) }
            is StmtAst.TryStmt -> {
                checkStmts(s.tryBody)
                checkStmts(s.catchBody)
                checkStmts(s.finallyBody)
            }
            // batch 语句：递归检查 body（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> checkStmts(s.body)
            is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> Unit
        }
    }

    private fun checkExpr(e: ExprAst) {
        when (e) {
            is ExprAst.LambdaExpr -> {
                val hasAwait = containsAwait(e.body)
                if (!e.isSuspend && hasAwait) {
                    diag.error(DslError.E625_SUSPEND_LAMBDA_MISSING_KEYWORD, e.position)
                } else if (e.isSuspend && !hasAwait) {
                    diag.warning(DslError.E627_SUSPEND_DECLARED_NO_AWAIT, e.position, "lambda")
                }
                checkStmts(e.body)
            }
            is ExprAst.CallExpr -> {
                checkExpr(e.callee)
                e.args.forEach { checkExpr(it) }
                e.namedArgs.forEach { checkExpr(it.value) }
            }
            is ExprAst.AwaitExpr -> {
                e.netRequestCall?.let { checkExpr(it) }
                e.suspendCall?.let { checkExpr(it) }
                e.tailExpr?.let { checkExpr(it) }
            }
            is ExprAst.BinaryOp -> { checkExpr(e.left); checkExpr(e.right) }
            is ExprAst.UnaryOp -> checkExpr(e.operand)
            is ExprAst.MemberAccess -> checkExpr(e.target)
            is ExprAst.SafeMemberAccess -> checkExpr(e.target)
            // M10 Stage 6：`target!!` 透传 target
            is ExprAst.NonNullAssert -> checkExpr(e.target)
            is ExprAst.MemberCall -> {
                checkExpr(e.target)
                e.args.forEach { checkExpr(it) }
                e.namedArgs.forEach { checkExpr(it.value) }
            }
            is ExprAst.SafeMemberCall -> {
                checkExpr(e.target)
                e.args.forEach { checkExpr(it) }
            }
            is ExprAst.Elvis -> { checkExpr(e.left); checkExpr(e.right) }
            is ExprAst.Ternary -> {
                checkExpr(e.cond)
                checkExpr(e.thenExpr)
                checkExpr(e.elseExpr)
            }
            is ExprAst.IfExpr -> {
                checkExpr(e.cond)
                checkStmts(e.thenBody)
                checkStmts(e.elseBody)
            }
            is ExprAst.Interp -> e.parts.forEach {
                if (it is ExprAst.Interp.InterpPart.ExprPart) checkExpr(it.expr)
            }
            is ExprAst.ListLiteralExpr -> e.elements.forEach { checkExpr(it) }
            is ExprAst.MapLiteralExpr -> e.entries.forEach { checkExpr(it.value) }
            is ExprAst.RangeExpr -> { checkExpr(e.start); checkExpr(e.endValue) }
            is ExprAst.IndexExpr -> { checkExpr(e.target); checkExpr(e.index) }
            is ExprAst.BuiltinCallExpr -> e.args.forEach { checkExpr(it) }
            is ExprAst.NetRequestCall -> {
                e.payload.forEach { checkExpr(it.value) }
                e.callback?.let { checkExpr(it) }
                e.timeoutMs?.let { checkExpr(it) }
            }
            is ExprAst.NetSubscribeCall -> checkExpr(e.handler)
            is ExprAst.WhenExpr -> {
                checkExpr(e.subject)
                e.branches.forEach { br ->
                    br.values.forEach { checkExpr(it) }
                    checkStmts(br.body)
                }
                e.elseBody?.let { checkStmts(it) }
            }
            is ExprAst.IsExpr -> checkExpr(e.target)
            is ExprAst.Literal, is ExprAst.NullLit, is ExprAst.IdentRef,
            is ExprAst.LocalRef, is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef, is ExprAst.PropsRef,
            is ExprAst.ThemeRef, is ExprAst.FunRefExpr -> Unit
        }
    }

    // ==================== 浅层 await / delay 检测（不递归进入嵌套 fun/lambda） ====================

    /** delay() 调用也需要 suspend 上下文。 */
    private fun isDelayCall(e: ExprAst): Boolean =
        e is ExprAst.BuiltinCallExpr && e.name == "delay"

    private fun containsAwait(stmts: List<StmtAst>): Boolean =
        stmts.any { containsAwaitInStmt(it) }

    private fun containsAwaitInStmt(s: StmtAst): Boolean = when (s) {
        is StmtAst.VarDeclStmt -> containsAwaitInExpr(s.initializer)
        is StmtAst.AssignStmt -> containsAwaitInExpr(s.rhs)
        is StmtAst.ExprStmt -> isDelayCall(s.call) || containsAwaitInExpr(s.call)
        is StmtAst.IfStmt ->
            containsAwaitInExpr(s.cond) || containsAwait(s.thenBody) || containsAwait(s.elseBody)
        is StmtAst.WhileStmt -> containsAwaitInExpr(s.cond) || containsAwait(s.body)
        is StmtAst.ForInStmt -> containsAwaitInExpr(s.iterable) || containsAwait(s.body)
        is StmtAst.WhenStmt -> containsAwaitInExpr(s.whenExpr)
        is StmtAst.ReturnStmt -> s.value?.let { containsAwaitInExpr(it) } ?: false
        is StmtAst.TryStmt ->
            containsAwait(s.tryBody) || containsAwait(s.catchBody) || containsAwait(s.finallyBody)
        // batch 语句：递归检测 body 是否含 await（与 TryStmt 的 tryBody 处理一致）
        is StmtAst.BatchStmt -> containsAwait(s.body)
        is StmtAst.FunDeclStmt -> false
        is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> false
    }

    private fun containsAwaitInExpr(e: ExprAst): Boolean = when (e) {
        is ExprAst.AwaitExpr -> true
        is ExprAst.LambdaExpr -> false
        is ExprAst.CallExpr ->
            containsAwaitInExpr(e.callee) ||
                    e.args.any { containsAwaitInExpr(it) } ||
                    e.namedArgs.any { containsAwaitInExpr(it.value) }
        is ExprAst.BinaryOp -> containsAwaitInExpr(e.left) || containsAwaitInExpr(e.right)
        is ExprAst.UnaryOp -> containsAwaitInExpr(e.operand)
        is ExprAst.MemberAccess -> containsAwaitInExpr(e.target)
        is ExprAst.SafeMemberAccess -> containsAwaitInExpr(e.target)
        // M10 Stage 6：`target!!` 透传 target
        is ExprAst.NonNullAssert -> containsAwaitInExpr(e.target)
        is ExprAst.MemberCall ->
            containsAwaitInExpr(e.target) || e.args.any { containsAwaitInExpr(it) }
                || e.namedArgs.any { containsAwaitInExpr(it.value) }
        is ExprAst.SafeMemberCall ->
            containsAwaitInExpr(e.target) || e.args.any { containsAwaitInExpr(it) }
        is ExprAst.Elvis -> containsAwaitInExpr(e.left) || containsAwaitInExpr(e.right)
        is ExprAst.Ternary ->
            containsAwaitInExpr(e.cond) ||
                    containsAwaitInExpr(e.thenExpr) ||
                    containsAwaitInExpr(e.elseExpr)
        is ExprAst.IfExpr ->
            containsAwaitInExpr(e.cond) || containsAwait(e.thenBody) || containsAwait(e.elseBody)
        is ExprAst.Interp ->
            e.parts.any { it is ExprAst.Interp.InterpPart.ExprPart && containsAwaitInExpr(it.expr) }
        is ExprAst.ListLiteralExpr -> e.elements.any { containsAwaitInExpr(it) }
        is ExprAst.MapLiteralExpr -> e.entries.any { containsAwaitInExpr(it.value) }
        is ExprAst.RangeExpr ->
            containsAwaitInExpr(e.start) || containsAwaitInExpr(e.endValue)
        is ExprAst.IndexExpr ->
            containsAwaitInExpr(e.target) || containsAwaitInExpr(e.index)
        is ExprAst.BuiltinCallExpr -> e.args.any { containsAwaitInExpr(it) }
        is ExprAst.NetRequestCall ->
            e.payload.any { containsAwaitInExpr(it.value) } ||
                    (e.callback?.let { containsAwaitInExpr(it) } ?: false) ||
                    (e.timeoutMs?.let { containsAwaitInExpr(it) } ?: false)
        is ExprAst.NetSubscribeCall -> containsAwaitInExpr(e.handler)
        is ExprAst.WhenExpr ->
            containsAwaitInExpr(e.subject) ||
                    e.branches.any { br ->
                        br.values.any { containsAwaitInExpr(it) } || containsAwait(br.body)
                    } ||
                    (e.elseBody?.let { containsAwait(it) } ?: false)
        is ExprAst.IsExpr -> containsAwaitInExpr(e.target)
        is ExprAst.Literal, is ExprAst.NullLit, is ExprAst.IdentRef,
        is ExprAst.LocalRef, is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef, is ExprAst.PropsRef,
        is ExprAst.ThemeRef, is ExprAst.FunRefExpr -> false
    }
}
